import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import {
  Grid,
  Typography,
  Paper,
  Button,
  Box,
  Tabs,
  Tab,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CreateInvoice from './CreateInvoice';
import ClientSubscriptions from './ClientSubscriptions';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import Chart from 'chart.js/auto';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import api from '../api'; // Assuming api.js is in the parent directory

const Billing = ({ setHideHeader, clientId, clientName: clientNameProp }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [userAssignedClients, setUserAssignedClients] = useState([]);
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || '');
  const sections = [
    'Dashboard',
    'Invoices',
    'Subscriptions',
    'Payments',
    'Credits',
    'Expenses',
    'Statements',
    'Payment Settings',
  ];

//  const [pdfData, setPdfData] = useState(null);
//  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState('Dashboard');
  const [statements, setStatements] = useState([]);
  const [filterBy, setFilterBy] = useState('All Time');
  const [status, setStatus] = useState('All Invoices');
  const [invoicePage, setInvoicePage] = useState('list');
  const [invoices, setInvoices] = useState([]);
  const [hasFetchedInvoices, setHasFetchedInvoices] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    paymentData: [],
    totalOutstanding: 0,
    totalPastDue: 0,
    outstandingInvoices: [],
    recentPaidInvoices: [],
  });
  const [clientName, setClientName] = useState(clientNameProp || '');
  const [chartKey, setChartKey] = useState(Date.now());
  const [credits, setCredits] = useState([]); // State for credits
  const [isCreditPreview, setIsCreditPreview] = useState(false); // New state to track preview type

  const [bankAccounts, setBankAccounts] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [openBankDialog, setOpenBankDialog] = useState(false);
  const [openCardDialog, setOpenCardDialog] = useState(false);
  const [bankForm, setBankForm] = useState({
    accountHolderName: '',
    bankName: '',
    routingNumber: '',
    abaRouting: '',
    accountNumber: '',
    accountType: 'CHECKING',
    nickname: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
  });
  const [cardForm, setCardForm] = useState({
    cardholderName: '',
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cvv: '',
    nickname: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
  });

  // State for Payments Collected section
  const [paymentsCollected, setPaymentsCollected] = useState([]);
  const [paymentStartDate, setPaymentStartDate] = useState('2025-01-01'); // Default to start of 2025
  const [paymentEndDate, setPaymentEndDate] = useState('2025-12-31'); // Default to end of 2025
  const [hasFetchedPayments, setHasFetchedPayments] = useState(false);

  // State for Payment Invoices section
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [hasFetchedPaidInvoices, setHasFetchedPaidInvoices] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const currentMonth = new Date().getMonth() + 1; // June 2025
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const ytdPaymentData = dashboardData.paymentData.slice(0, currentMonth);
  const currencyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  const paymentSeries = dashboardData.paymentData || [];
  const monthIdx = currentMonth - 1;
  const thisMonthPaymentAmt = paymentSeries[monthIdx]?.amount ?? 0;
  const prevMonthPaymentAmt = monthIdx > 0 ? (paymentSeries[monthIdx - 1]?.amount ?? 0) : 0;
  const paymentMonthDelta = thisMonthPaymentAmt - prevMonthPaymentAmt;

  useEffect(() => {
    fetchUserPermissions();
    fetchUserAssignedClients();
    if (clientId) {
      if (clientNameProp) {
        setClientName(clientNameProp);
      } else {
        fetchClientName();
      }
    }
  }, [clientId]);

  // Update client name when parent passes it (e.g. after clientData loads)
  useEffect(() => {
    if (clientNameProp) {
      setClientName(clientNameProp);
    }
  }, [clientNameProp]);

  useEffect(() => {
    if (selectedSection === 'Invoices' && !hasFetchedInvoices && clientId && userPermissions) {
      fetchInvoices();
      fetchDashboardData();
    }
    if (selectedSection === 'Dashboard' && clientId) {
      fetchDashboardData();
    }
    if (selectedSection === 'Payment Settings' && clientId) {
      fetchPaymentMethods();
    }
    if (selectedSection === 'Payments' && clientId) {
      if (!hasFetchedPayments) {
        fetchPaymentsCollected();
      }
      if (!hasFetchedPaidInvoices) {
        fetchPaidInvoices();
      }
      fetchPaymentMethods();
    }
    if (selectedSection === 'Credits' && clientId) {
      fetchCredits(); // Fetch credits when Credits section is selected
    }
  }, [selectedSection, hasFetchedInvoices, clientId, hasFetchedPayments, hasFetchedPaidInvoices, userPermissions]);

  const fetchUserAssignedClients = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        return;
      }
      const response = await api.get(`/api/multi-tenant/users/${userId}/assigned-clients/`);
      setUserAssignedClients(response.data.assigned_clients || []);
    } catch (error) {
      // Error fetching assigned clients
    }
  };

  const isClientAccessible = userAssignedClients.some(client => client.id === parseInt(clientId));

  useEffect(() => {
    // YTD payment data updated
  }, [ytdPaymentData]);

  const fetchClientName = async () => {
    try {
      const response = await api.get(`/api/multi-tenant/client-name/?clientId=${clientId}`);
      setClientName(response.data.clientName || `Client #${clientId}`);
    } catch (error) {
      setClientName(`Client #${clientId}`);
    }
  };

  // Block access for staff_assigned_clients_no_billing
  if (userRole === 'staff_assigned_clients_no_billing') {
    return <Typography color="error">You do not have permission to access the billing section.</Typography>;
  }

  const restrictToAssignedClients = () => {
    const restrictedRoles = ['staff_assigned_clients'];
    return restrictedRoles.includes(userRole) && !isClientAccessible;
  };

  if (restrictToAssignedClients()) {
    return <Typography color="error">You do not have permission to access this client.</Typography>;
  }

  const fetchDashboardData = async (retries = 3) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/multi-tenant/clients/${clientId}/billing-dashboard/`);
      setDashboardData(response.data);
      setChartKey(Date.now());
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchDashboardData(retries - 1), 1000);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPermissions = async () => {
    try {
      // Check multiple possible locations for user ID
      let userId = localStorage.getItem('userId');

      // If userId is not found, try to get it from the user object
      if (!userId) {
        const userData = localStorage.getItem('user');

        if (userData) {
          try {
            const user = JSON.parse(userData);
            userId = user.id;
          } catch (e) {
            // Error parsing user data
          }
        }
      }

      if (!userId) {
        return;
      }
      const response = await api.get(`/api/multi-tenant/users/?user_id=${userId}`);
      const userData = response.data;
      setUserPermissions(userData.permissions || {});
    } catch (error) {
      // Error fetching user permissions
    }
  };

  const fetchInvoices = async () => {
    if (!userPermissions?.can_view_invoices) {
      setError('You do not have permission to view invoices');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/multi-tenant/get-invoices/?clientId=${clientId}`);
      if (response.status === 200) {
        setInvoices(response.data);
        setHasFetchedInvoices(true);
      } else {
        setError('Failed to fetch invoices.');
      }
    } catch (err) {
      setError('Error fetching invoices.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get(`/api/multi-tenant/clients/${clientId}/payment-methods/`);
      setBankAccounts(response.data.bank_accounts || []);
      setCreditCards(response.data.credit_cards || []);
    } catch (err) {
      setError('Error fetching payment methods.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentsCollected = async () => {
    if (!userPermissions?.can_access_payments) {
      setError('You do not have permission to access payments');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/api/multi-tenant/payments/collected/?clientId=${clientId}&startDate=${paymentStartDate}&endDate=${paymentEndDate}`
      );
      setPaymentsCollected(response.data || []);
      setHasFetchedPayments(true);
    } catch (err) {
      setError('Error fetching payments collected.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaidInvoices = async () => {
    if (!userPermissions?.can_access_payments) {
      setError('You do not have permission to access payments');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/api/multi-tenant/get-invoices/?clientId=${clientId}&status=Paid`
      );
      if (response.status === 200) {
        const data = response.data;
        setPaidInvoices(data || []);
        setHasFetchedPaidInvoices(true);
      } else {
        setError('Failed to fetch paid invoices.');
      }
    } catch (err) {
      setError('Error fetching paid invoices.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCredits = async () => {
    if (!userPermissions?.can_access_credits) {
      setError('You do not have permission to access credits');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Check multiple possible locations for user ID (same logic as fetchUserPermissions)
      let userId = localStorage.getItem('userId');

      // If userId is not found, try to get it from the user object
      if (!userId) {
        const userData = localStorage.getItem('user');

        if (userData) {
          try {
            const user = JSON.parse(userData);
            userId = user.id;
          } catch (e) {
            // Error parsing user data
          }
        }
      }

      if (!userId || !clientId) {
        throw new Error('User ID or Client ID not available');
      }
      
      const response = await api.get(`/api/multi-tenant/clients/${clientId}/credits/?user=${userId}`);
      const rawCredits = Array.isArray(response.data) ? response.data : (response.data?.credits || []);
      const creditsList = rawCredits.map(c => {
        const raw = c.credit_remaining ?? c.creditRemaining ?? 0;
        const num = typeof raw === 'number' ? raw : parseFloat(raw);
        return {
          ...c,
          creditNumber: c.credit_number ?? c.creditNumber,
          creditRemaining: (typeof num === 'number' && !isNaN(num) ? num : 0),
        };
      });
      setCredits(creditsList);
    } catch (error) {
      setError(`Failed to load credits: ${error.response ? error.response.statusText : error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBankAccount = async () => {
    try {
      const response = await api.post(`/api/multi-tenant/add-bank-account/`, {
        clientId: clientId,
        accountHolderName: bankForm.accountHolderName,
        bankName: bankForm.bankName,
        routingNumber: bankForm.routingNumber,
        abaRouting: bankForm.abaRouting,
        accountNumber: bankForm.accountNumber,
        accountType: bankForm.accountType,
        nickname: bankForm.nickname,
        streetAddress: bankForm.streetAddress,
        city: bankForm.city,
        state: bankForm.state,
        zipCode: bankForm.zipCode,
        country: bankForm.country,
      });
      
      if (response.status === 201) {
        setOpenBankDialog(false);
        setBankForm({
          accountHolderName: '',
          bankName: '',
          routingNumber: '',
          abaRouting: '',
          accountNumber: '',
          accountType: 'CHECKING',
          nickname: '',
          streetAddress: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'United States',
        });
        fetchPaymentMethods();
      }
    } catch (error) {
      // Error adding bank account
    }
  };

  const handleAddCreditCard = async () => {
    try {
      const response = await api.post(`/api/multi-tenant/add-credit-card/`, {
        clientId: clientId,
        cardholderName: cardForm.cardholderName,
        cardNumber: cardForm.cardNumber,
        expMonth: cardForm.expMonth,
        expYear: cardForm.expYear,
        cvv: cardForm.cvv,
        nickname: cardForm.nickname,
        streetAddress: cardForm.streetAddress,
        city: cardForm.city,
        state: cardForm.state,
        zipCode: cardForm.zipCode,
        country: cardForm.country,
      });
      
      if (response.status === 201) {
        setOpenCardDialog(false);
        setCardForm({
          cardholderName: '',
          cardNumber: '',
          expMonth: '',
          expYear: '',
          cvv: '',
          nickname: '',
          streetAddress: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'United States',
        });
        fetchPaymentMethods();
      }
    } catch (error) {
      // Error adding credit card
    }
  };

  const handleDeleteBankAccount = async (accountId) => {
    try {
      await api.delete(`/api/multi-tenant/delete-bank-account/${accountId}/`);
      fetchPaymentMethods();
    } catch (error) {
      // Error deleting bank account
    }
  };

  const handleDeleteCreditCard = async (cardId) => {
    try {
      await api.delete(`/api/multi-tenant/delete-credit-card/${cardId}/`);
      fetchPaymentMethods();
    } catch (error) {
      // Error deleting credit card
    }
  };

  const handleGenerateClick = async () => {
    if (!userPermissions?.can_access_statements) {
      setError('You do not have permission to access statements');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/api/multi-tenant/get-invoices/?clientId=${clientId}&filterBy=${filterBy}&status=${status}`
      );
      if (response.status === 200) {
        const data = response.data;
        setInvoices(data || []);
      } else {
        setError('Failed to fetch data.');
      }
    } catch (err) {
      setError('Error fetching data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendForPayment = async (invoiceNumber, invoiceID) => {
    if (!userPermissions?.can_send_invoices_for_payment) {
      setError('You do not have permission to send invoices for payment');
      return;
    }

    try {
      const response = await api.post(
        `/api/multi-tenant/invoice-payment/`,
        { invoice_number: invoiceNumber, invoice_id: invoiceID }
      );

      if (response.status === 200) {
        alert(`Invoice #${invoiceNumber} sent for payment successfully!`);
        fetchDashboardData();
      } else {
        alert(`Failed to send invoice #${invoiceNumber} for payment.`);
      }
    } catch (error) {
      alert('An error occurred while sending the invoice for payment.');
    }
  };

  const handleSectionChange = (section) => setSelectedSection(section);
  const handleCreateInvoiceClick = () => {
    if (!userPermissions?.can_create_invoices) {
      setError('You do not have permission to create invoices');
      return;
    }
    setInvoicePage('create');
  };
  const handleBackToList = () => setInvoicePage('list');

  const handleInvoiceClick = async (invoiceId) => {
    try {
      setLoading(true);
      setIsCreditPreview(false); // Set to false for invoices
      const selectedInvoice = invoices.find(inv => inv.invoiceID === invoiceId);
      if (!selectedInvoice) throw new Error('Invoice not found in state');

      const invoiceData = {
        invoiceNumber: selectedInvoice.invoiceNumber,
        invoiceDate: selectedInvoice.date_issued || new Date().toISOString().split('T')[0],
        terms: 'Due on Receipt',
        dueDate: selectedInvoice.due_date || new Date().toISOString().split('T')[0],
        items: selectedInvoice.items || [{ service: 'Unknown', description: selectedInvoice.description || '', quantity: 1, rate: selectedInvoice.amount || 0, amount: selectedInvoice.amount || 0, tax: 0 }],
        clientName: clientName,
        street1: 'N/A',
        street2: '',
        zipcode: 'N/A',
        state: 'N/A',
        country: 'N/A',
        paymentInstructions: 'To avoid paying the CC/Debit fee, follow these steps:\n1. Either click on VIEW INVOICE or Pay Now.\n2. Click on Bank account versus the...',
      };

      const response = await api.post(`/api/multi-tenant/preview-invoice/`, invoiceData);
      setPdfData(`data:application/pdf;base64,${response.data.pdf}`);
      setSelectedInvoice(invoiceId);
    } catch (error) {
      setError('Failed to load invoice preview.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreditClick = async (creditId) => {
    try {
      setLoading(true);
      setIsCreditPreview(true); // Set to true for credits
      const response = await api.get(`/api/multi-tenant/credits/${creditId}/pdf/`);
      // Handle JSON response with base64 data
      if (response.status === 200 && response.data && response.data.pdf) {
        setPdfData(`data:application/pdf;base64,${response.data.pdf}`);
      } else {
        throw new Error('Invalid PDF data in response');
      }
      setSelectedInvoice(creditId);
    } catch (error) {
      setError('Failed to load credit preview.');
    } finally {
      setLoading(false);
    }
  };

  const handleClosePreview = () => {
    setSelectedInvoice(null);
    setPdfData(null);
    setIsCreditPreview(false); // Reset on close
  };

  const handleDeleteCredit = async (creditId) => {
    if (!window.confirm('Are you sure you want to delete this credit?')) return;

    try {
      // Check multiple possible locations for user ID (same logic as fetchUserPermissions)
      let userId = localStorage.getItem('userId');
      
      // If userId is not found, try to get it from the user object
      if (!userId) {
        const userData = localStorage.getItem('user');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            userId = user.id;
          } catch (e) {
            // Error parsing user data
          }
        }
      }

      if (!userId) {
        throw new Error('User ID not available');
      }
      
      await api.delete(`/api/multi-tenant/credits/${creditId}/?user=${userId}`);
      setCredits(credits.filter(credit => credit.id !== creditId));
    } catch (error) {
      setError(`Failed to delete credit: ${error.response ? error.response.statusText : error.message}`);
    }
  };

  if (!clientId) {
    return <Typography color="error">Client ID not provided.</Typography>;
  }

  if (invoicePage === 'create') {
    return <CreateInvoice onBack={handleBackToList} clientId={clientId} />;
  }

  const sectionPermissionMap = {
    Dashboard: true,
    Invoices: userPermissions?.can_view_invoices,
    Payments: userPermissions?.can_access_payments,
    Credits: userPermissions?.can_access_credits,
    Expenses: userPermissions?.can_access_expenses,
    Statements: userPermissions?.can_access_statements,
    Subscriptions: userPermissions?.can_view_invoices,
    'Payment Settings': userPermissions?.can_access_payment_settings,
  };

  const sectionIndex = sections.indexOf(selectedSection);

  const getInvoiceStatusDisplay = (invoice) => {
    const raw = String(invoice.invoice_status || invoice.status || '').toUpperCase();
    const paid = raw === 'DONE' || raw === 'PAID';
    let overdue = false;
    if (invoice.due_date && !paid) {
      const due = new Date(invoice.due_date);
      const today = new Date();
      due.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      overdue = due < today;
    }
    if (overdue && !paid) return { label: 'Overdue', color: 'error', isOverdue: true };
    if (paid) return { label: 'Paid', color: 'success', isOverdue: false };
    if (raw === 'SENT') return { label: 'Sent', color: 'info', isOverdue: false };
    return { label: 'Draft', color: 'default', isOverdue: false };
  };

  return (
    <Box sx={{ bgcolor: 'background.default', p: 2 }}>
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Tabs
          value={sectionIndex >= 0 ? sectionIndex : 0}
          onChange={(e, v) => handleSectionChange(sections[v])}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {sections.map((section, index) => (
            <Tab
              key={section}
              label={section}
              disabled={!sectionPermissionMap[section]}
              sx={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'none' }}
            />
          ))}
        </Tabs>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, boxShadow: 1, border: 1, borderColor: 'divider' }}>
          {selectedSection === 'Dashboard' && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Dashboard
                </Typography>
                <Tooltip title="Refresh">
                  <span>
                    <IconButton color="secondary" onClick={() => fetchDashboardData()} disabled={loading} aria-label="Refresh dashboard">
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {/* Summary Cards Row */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', boxShadow: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Outstanding Balance</Typography>
                    <Typography variant="h4" color="primary" fontWeight={700}>
                      {currencyFmt.format(dashboardData.totalOutstanding || 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', boxShadow: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Past Due</Typography>
                    <Typography variant="h4" color="error" fontWeight={700}>
                      {currencyFmt.format(dashboardData.totalPastDue || 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', boxShadow: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Payments Collected (YTD)</Typography>
                    <Typography variant="h4" color="success.main" fontWeight={700}>
                      {currencyFmt.format(ytdPaymentData.reduce((sum, item) => sum + (item.amount || 0), 0))}
                    </Typography>
                    {monthIdx > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                        {paymentMonthDelta >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 18, color: 'error.main' }} />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {currencyFmt.format(Math.abs(paymentMonthDelta))} vs prior month
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', boxShadow: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Open Invoices</Typography>
                    <Typography variant="h4" color="info.main" fontWeight={700}>
                      {dashboardData.outstandingInvoices.length}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              {/* Chart + Outstanding Invoices side by side on md+, chart constrained height */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={5}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>YTD Revenue</Typography>
                    {ytdPaymentData.length > 0 ? (
                      <Box sx={{ height: 220, position: 'relative' }} key={chartKey}>
                        <Line
                          data={{
                            labels: ytdPaymentData.map(item => item.month || monthNames[item.monthIndex - 1]),
                            datasets: [
                              {
                                label: 'Payments Received ($)',
                                data: ytdPaymentData.map(item => item.amount || 0),
                                borderColor: primaryColor,
                                backgroundColor: primaryColor,
                                fill: false,
                                pointRadius: 5,
                                pointHoverRadius: 7,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              tooltip: {
                                callbacks: {
                                  label: function(context) {
                                    const label = context.label || '';
                                    const value = context.parsed.y || 0;
                                    return `${label}: $${value.toLocaleString()}`;
                                  },
                                },
                              },
                              legend: { display: false },
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                title: { display: true, text: 'Payments ($)' },
                                ticks: {
                                  callback: function(value) {
                                    return `$${value.toLocaleString()}`;
                                  },
                                },
                              },
                              x: {
                                title: { display: true, text: 'Month' },
                              },
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No payment data available for this year.
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Paper sx={{ p: 2 }}>
                <Typography variant="h6" mb={2}>Outstanding Invoices</Typography>
                {dashboardData.outstandingInvoices.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Invoice Number</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Due Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dashboardData.outstandingInvoices.map((invoice, index) => (
                          <TableRow key={index}>
                            <TableCell>{invoice.invoiceNumber}</TableCell>
                            <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                            <TableCell>{new Date(invoice.due_date).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No outstanding invoices.
                  </Typography>
                )}
                  </Paper>
                </Grid>
              </Grid>
              {/* Recent Paid Invoices */}
              <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" mb={2}>Recent Paid Invoices</Typography>
                {(dashboardData.recentPaidInvoices || []).length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Invoice Number</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Payment Date</TableCell>
                          <TableCell>Payment Method</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(dashboardData.recentPaidInvoices || []).map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell>{invoice.invoiceNumber}</TableCell>
                            <TableCell>${(invoice.amount || 0).toFixed(2)}</TableCell>
                            <TableCell>{invoice.payment_date ? new Date(invoice.payment_date).toLocaleDateString() : 'N/A'}</TableCell>
                            <TableCell>{invoice.payment_method || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No paid invoices yet.
                  </Typography>
                )}
              </Paper>
            </Box>
          )}

          {selectedSection === 'Invoices' && (
            <>
              <Box mb={4}>
                <Typography variant="h4" gutterBottom>
                  Invoices for {clientName}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">YTD Revenue</Typography>
                      {ytdPaymentData.length > 0 ? (
                        <Box mt={2} key={chartKey}>
                          <Line
                            data={{
                              labels: ytdPaymentData.map(item => item.month || monthNames[item.monthIndex - 1]),
                              datasets: [
                                {
                                  label: 'Payments Received ($)',
                                  data: ytdPaymentData.map(item => item.amount || 0),
                                  borderColor: primaryColor,
                                  backgroundColor: theme.palette.primary.light,
                                  fill: false,
                                  pointRadius: 5,
                                  pointHoverRadius: 7,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              plugins: {
                                tooltip: {
                                  callbacks: {
                                    label: function(context) {
                                      const label = context.label || '';
                                      const value = context.parsed.y || 0;
                                      return `${label}: $${value.toLocaleString()}`;
                                    },
                                  },
                                },
                                legend: {
                                  display: false,
                                },
                              },
                              scales: {
                                y: {
                                  beginAtZero: true,
                                  title: { display: true, text: 'Payments ($)' },
                                  ticks: {
                                    callback: function(value) {
                                      return `$${value.toLocaleString()}`;
                                    },
                                  },
                                },
                                x: {
                                  title: { display: true, text: 'Month' },
                                },
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary" mt={2}>
                          No payment data available for this year.
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">Total Past Due</Typography>
                      <Typography variant="h5" color="secondary">
                        ${dashboardData.totalPastDue.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Unresolved dues
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h6">Total Outstandings</Typography>
                      <Typography variant="h5" color="textPrimary">
                        ${dashboardData.totalOutstanding.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Engaged this month
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Box mt={3}>
                  <Typography variant="h6">Performance Insights</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Monitor your financial health and business trends over time.
                  </Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom={2}
              >
                <Box />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCreateInvoiceClick}
                  disabled={!userPermissions?.can_create_invoices}
                >
                  Create Invoice
                </Button>
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error" align="center">
                  {error}
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice Number</TableCell>
                        <TableCell>Invoice ID</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.length > 0 ? (
                        invoices.map((invoice, index) => {
                          const invStatus = getInvoiceStatusDisplay(invoice);
                          const canSend = userPermissions?.can_send_invoices_for_payment;
                          const paidLike = (invoice.invoice_status || invoice.status) === 'DONE' || invoice.status === 'PAID';
                          const sentLike = (invoice.invoice_status || invoice.status) === 'SENT';
                          return (
                          <TableRow
                            key={index}
                            onClick={() => handleInvoiceClick(invoice.invoiceID)}
                            style={{ cursor: 'pointer' }}
                          >
                            <TableCell>{invoice.invoiceNumber}</TableCell>
                            <TableCell>{invoice.invoiceID}</TableCell>
                            <TableCell>{invoice.description}</TableCell>
                            <TableCell>
                              <Chip label={invStatus.label} size="small" color={invStatus.color} variant={invStatus.color === 'default' ? 'outlined' : 'filled'} />
                            </TableCell>
                            <TableCell>{currencyFmt.format(parseFloat(invoice.amount) || 0)}</TableCell>
                            <TableCell>{invoice.due_date}</TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                {invStatus.isOverdue && canSend ? (
                                  <Button
                                    type="button"
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleSendForPayment(invoice.invoiceNumber, invoice.invoiceID);
                                    }}
                                  >
                                    Pay Now
                                  </Button>
                                ) : (
                                  <Button
                                    type="button"
                                    variant="outlined"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleSendForPayment(invoice.invoiceNumber, invoice.invoiceID);
                                    }}
                                    disabled={sentLike || paidLike || !canSend}
                                  >
                                    {sentLike ? 'Sent' : 'Send for Payment'}
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No invoices found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {selectedSection === 'Subscriptions' && clientId && (
            <ClientSubscriptions clientId={clientId} clientName={clientName} />
          )}

          {selectedSection === 'Credits' && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h4" gutterBottom>
                Credits for {clientName}
              </Typography>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error" align="center">
                  {error}
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Credit Number</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Credit Remaining ($)</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {credits.length > 0 ? (
                        credits.map((credit) => (
                          <TableRow
                            key={credit.id}
                            onClick={(e) => { e.stopPropagation(); handleCreditClick(credit.id); }}
                            style={{ cursor: 'pointer' }}
                          >
                            <TableCell>{credit.creditNumber}</TableCell>
                            <TableCell>{credit.description}</TableCell>
                            <TableCell>${(parseFloat(credit?.creditRemaining ?? 0) || 0).toFixed(2)}</TableCell>
                            <TableCell>{new Date(credit.date).toLocaleDateString()}</TableCell>
                            <TableCell>{credit.status}</TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => handleDeleteCredit(credit.id)}
                                disabled={!userPermissions?.can_access_credits}
                              >
                                <DeleteIcon color="error" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No credits available.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}

          {selectedSection === 'Payments' && (
            <Box>
              <Typography variant="h4" gutterBottom>
                Payments for {clientName}
              </Typography>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error" align="center">
                  {error}
                </Typography>
              ) : (
                <>
                  {/* Payments Collected Subsection */}
                  <Box mb={4}>
                    <Typography variant="h6" gutterBottom>
                      Payments Collected
                    </Typography>
                    <Grid container spacing={2} alignItems="center" mb={2}>
                      <Grid item>
                        <TextField
                          label="Start Date"
                          type="date"
                          value={paymentStartDate}
                          onChange={(e) => setPaymentStartDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 180 }}
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          label="End Date"
                          type="date"
                          value={paymentEndDate}
                          onChange={(e) => setPaymentEndDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 180 }}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setHasFetchedPayments(false);
                            fetchPaymentsCollected();
                          }}
                        >
                          Filter
                        </Button>
                      </Grid>
                    </Grid>
                    <TableContainer component={Paper}>
                      <Table>
                        {paymentsCollected.length > 0 && (
                          <TableHead>
                            <TableRow>
                              <TableCell>Payment Date</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Invoice Number</TableCell>
                            </TableRow>
                          </TableHead>
                        )}
                        <TableBody>
                          {paymentsCollected.length > 0 ? (
                            paymentsCollected.map((payment, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  {new Date(payment.paymentDate).toLocaleDateString()}
                                </TableCell>
                                <TableCell>${payment.amount.toFixed(2)}</TableCell>
                                <TableCell>{payment.invoiceNumber || 'N/A'}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                No payments collected in this date range.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Payment Methods Subsection */}
                  <Box mb={4}>
                    <Typography variant="h6" gutterBottom>
                      Payment Methods
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      Saved Bank Accounts
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                      <Table>
                        {bankAccounts.length > 0 && (
                          <TableHead>
                            <TableRow>
                              <TableCell>Nickname</TableCell>
                              <TableCell>Bank Name</TableCell>
                              <TableCell>Account Type</TableCell>
                              <TableCell>Last 4 Digits</TableCell>
                            </TableRow>
                          </TableHead>
                        )}
                        <TableBody>
                          {bankAccounts.length > 0 ? (
                            bankAccounts.map((account) => (
                              <TableRow key={account.id}>
                                <TableCell>{account.nickname}</TableCell>
                                <TableCell>{account.bankName}</TableCell>
                                <TableCell>{account.accountType}</TableCell>
                                <TableCell>{account.lastFourDigits}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                No bank accounts added.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Typography variant="subtitle1" gutterBottom>
                      Saved Credit/Debit Cards
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        {creditCards.length > 0 && (
                          <TableHead>
                            <TableRow>
                              <TableCell>Nickname</TableCell>
                              <TableCell>Card Type</TableCell>
                              <TableCell>Last 4 Digits</TableCell>
                              <TableCell>Expiration Date</TableCell>
                            </TableRow>
                          </TableHead>
                        )}
                        <TableBody>
                          {creditCards.length > 0 ? (
                            creditCards.map((card) => (
                              <TableRow key={card.id}>
                                <TableCell>{card.nickname}</TableCell>
                                <TableCell>{card.cardType || 'N/A'}</TableCell>
                                <TableCell>{card.lastFourDigits}</TableCell>
                                <TableCell>
                                  {new Date(card.expirationDate).toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' })}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                No credit/debit cards added.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Payment Invoices Subsection */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Paid Invoices
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        {paidInvoices.length > 0 && (
                          <TableHead>
                            <TableRow>
                              <TableCell>Invoice Number</TableCell>
                              <TableCell>Invoice ID</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Payment Date</TableCell>
                            </TableRow>
                          </TableHead>
                        )}
                        <TableBody>
                          {paidInvoices.length > 0 ? (
                            paidInvoices.map((invoice, index) => (
                              <TableRow key={invoice.invoiceID || index}>
                                <TableCell>{invoice.invoiceNumber}</TableCell>
                                <TableCell>{invoice.invoiceID}</TableCell>
                                <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                                <TableCell>{invoice.status}</TableCell>
                                <TableCell>
                                  {invoice.payment_date
                                    ? new Date(invoice.payment_date).toLocaleDateString()
                                    : 'N/A'}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                No paid invoices found.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              )}
            </Box>
          )}

          {selectedSection === 'Payment Settings' && (
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
                Payment Settings for {clientName}
              </Typography>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="260px">
                  <CircularProgress size={40} />
                </Box>
              ) : error ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'error.lighter' }}>
                  <Typography color="error">{error}</Typography>
                </Paper>
              ) : (
                <>
                  <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setOpenBankDialog(true)}
                      sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}
                    >
                      Add Bank Account
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setOpenCardDialog(true)}
                      sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}
                    >
                      Add Credit/Debit Card
                    </Button>
                  </Box>

                  <Paper sx={{ mb: 4, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid divider' }}>
                  <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderBottom: '1px solid divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Saved Bank Accounts
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Manage bank accounts for ACH payments</Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table stickyHeader={bankAccounts.length > 0} size="small">
                      {bankAccounts.length > 0 && (
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Nickname</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Bank Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Account Type</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Last 4 Digits</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                      )}
                      <TableBody>
                        {bankAccounts.length > 0 ? (
                          bankAccounts.map((account) => (
                            <TableRow key={account.id}>
                              <TableCell>{account.nickname}</TableCell>
                              <TableCell>{account.bankName}</TableCell>
                              <TableCell>{account.accountType}</TableCell>
                              <TableCell>{account.lastFourDigits}</TableCell>
                              <TableCell>
                                <IconButton onClick={() => handleDeleteBankAccount(account.id)}>
                                  <DeleteIcon color="error" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No bank accounts added.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </Paper>

                  <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid divider' }}>
                  <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderBottom: '1px solid divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Saved Credit/Debit Cards
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Manage cards for invoice payments</Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table stickyHeader={creditCards.length > 0} size="small">
                      {creditCards.length > 0 && (
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Nickname</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Card Type</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Last 4 Digits</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Expiration Date</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                      )}
                      <TableBody>
                        {creditCards.length > 0 ? (
                          creditCards.map((card) => (
                            <TableRow key={card.id}>
                              <TableCell>{card.nickname}</TableCell>
                              <TableCell>{card.cardType || 'N/A'}</TableCell>
                              <TableCell>{card.lastFourDigits}</TableCell>
                              <TableCell>
                                {new Date(card.expirationDate).toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' })}
                              </TableCell>
                              <TableCell>
                                <IconButton onClick={() => handleDeleteCreditCard(card.id)}>
                                  <DeleteIcon color="error" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No credit/debit cards added.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </Paper>

                  <Dialog open={openBankDialog} onClose={() => setOpenBankDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Add Bank Account</DialogTitle>
                    <DialogContent>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <TextField
                            label="Account Holder Name"
                            fullWidth
                            value={bankForm.accountHolderName}
                            onChange={(e) => setBankForm({ ...bankForm, accountHolderName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Bank Name"
                            fullWidth
                            value={bankForm.bankName}
                            onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Routing Number"
                            fullWidth
                            value={bankForm.routingNumber}
                            onChange={(e) => setBankForm({ ...bankForm, routingNumber: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="ABA Routing"
                            fullWidth
                            value={bankForm.abaRouting}
                            onChange={(e) => setBankForm({ ...bankForm, abaRouting: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Account Number"
                            fullWidth
                            value={bankForm.accountNumber}
                            onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Account Type</InputLabel>
                            <Select
                              value={bankForm.accountType}
                              onChange={(e) => setBankForm({ ...bankForm, accountType: e.target.value })}
                            >
                              <MenuItem value="CHECKING">Checking</MenuItem>
                              <MenuItem value="SAVINGS">Savings</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Nickname"
                            fullWidth
                            value={bankForm.nickname}
                            onChange={(e) => setBankForm({ ...bankForm, nickname: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Street Address"
                            fullWidth
                            value={bankForm.streetAddress}
                            onChange={(e) => setBankForm({ ...bankForm, streetAddress: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="City"
                            fullWidth
                            value={bankForm.city}
                            onChange={(e) => setBankForm({ ...bankForm, city: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="State"
                            fullWidth
                            value={bankForm.state}
                            onChange={(e) => setBankForm({ ...bankForm, state: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="ZIP Code"
                            fullWidth
                            value={bankForm.zipCode}
                            onChange={(e) => setBankForm({ ...bankForm, zipCode: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Country"
                            fullWidth
                            value={bankForm.country}
                            onChange={(e) => setBankForm({ ...bankForm, country: e.target.value })}
                          />
                        </Grid>
                      </Grid>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setOpenBankDialog(false)}>Cancel</Button>
                      <Button onClick={handleAddBankAccount} variant="contained" color="primary">
                        Save
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <Dialog open={openCardDialog} onClose={() => setOpenCardDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Add Credit/Debit Card</DialogTitle>
                    <DialogContent>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <TextField
                            label="Cardholder Name"
                            fullWidth
                            value={cardForm.cardholderName}
                            onChange={(e) => setCardForm({ ...cardForm, cardholderName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Card Number"
                            fullWidth
                            value={cardForm.cardNumber}
                            onChange={(e) => setCardForm({ ...cardForm, cardNumber: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel>Expiration Month</InputLabel>
                            <Select
                              value={cardForm.expMonth}
                              onChange={(e) => setCardForm({ ...cardForm, expMonth: e.target.value })}
                            >
                              {[...Array(12)].map((_, i) => (
                                <MenuItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                                  {String(i + 1).padStart(2, '0')}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel>Expiration Year</InputLabel>
                            <Select
                              value={cardForm.expYear}
                              onChange={(e) => setCardForm({ ...cardForm, expYear: e.target.value })}
                            >
                              {[...Array(11)].map((_, i) => (
                                <MenuItem key={2025 + i} value={String(2025 + i)}>
                                  {2025 + i}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="CVV"
                            fullWidth
                            value={cardForm.cvv}
                            onChange={(e) => setCardForm({ ...cardForm, cvv: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Nickname"
                            fullWidth
                            value={cardForm.nickname}
                            onChange={(e) => setCardForm({ ...cardForm, nickname: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Street Address"
                            fullWidth
                            value={cardForm.streetAddress}
                            onChange={(e) => setCardForm({ ...cardForm, streetAddress: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="City"
                            fullWidth
                            value={cardForm.city}
                            onChange={(e) => setCardForm({ ...cardForm, city: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="State"
                            fullWidth
                            value={cardForm.state}
                            onChange={(e) => setCardForm({ ...cardForm, state: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="ZIP Code"
                            fullWidth
                            value={cardForm.zipCode}
                            onChange={(e) => setCardForm({ ...cardForm, zipCode: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Country"
                            fullWidth
                            value={cardForm.country}
                            onChange={(e) => setCardForm({ ...cardForm, country: e.target.value })}
                          />
                        </Grid>
                      </Grid>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setOpenCardDialog(false)}>Cancel</Button>
                      <Button onClick={handleAddCreditCard} variant="contained" color="primary">
                        Save
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
            </Box>
          )}

          {selectedSection === 'Expenses' && (
            <Typography variant="h5">Expenses section is under development.</Typography>
          )}

          {selectedSection === 'Statements' && (
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
                Statements for {clientName}
              </Typography>
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>Filter Options</Typography>
                <Grid container spacing={3} alignItems="center" wrap="wrap">
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small" variant="outlined" sx={{ minWidth: 180 }}>
                      <InputLabel>Filter By</InputLabel>
                      <Select value={filterBy} onChange={(e) => setFilterBy(e.target.value)} label="Filter By">
                        <MenuItem value="All Time">All Time</MenuItem>
                        <MenuItem value="Last Month">Last Month</MenuItem>
                        <MenuItem value="Last Year">Last Year</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small" variant="outlined" sx={{ minWidth: 180 }}>
                      <InputLabel>Status</InputLabel>
                      <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
                        <MenuItem value="All Invoices">All Invoices</MenuItem>
                        <MenuItem value="Paid">Paid</MenuItem>
                        <MenuItem value="Unpaid">Unpaid</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button variant="contained" color="primary" onClick={handleGenerateClick} sx={{ borderRadius: 2, fontWeight: 600 }}>
                      Generate Statement
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {!loading && !error && invoices.length > 0 && (
                <Paper sx={{ p: 0, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid divider' }}>
                <TableContainer sx={{ maxHeight: 440 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Invoice ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Customer ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date Issued</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((invoice, index) => (
                        <TableRow key={invoice.invoiceID || index}>
                          <TableCell>{invoice.invoiceNumber}</TableCell>
                          <TableCell>{invoice.invoiceID}</TableCell>
                          <TableCell>
                            {invoice.amount} {invoice.currency || 'USD'}
                          </TableCell>
                          <TableCell>{invoice.status}</TableCell>
                          <TableCell>{invoice.customerid}</TableCell>
                          <TableCell>
                            {invoice.date_issued
                              ? new Date(invoice.date_issued).toLocaleString()
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Paper>
              )}

              {!loading && !error && invoices.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'grey.50' }}>
                  <Typography color="textSecondary">No invoices found for the selected criteria.</Typography>
                </Paper>
              )}
            </Box>
          )}

          {selectedSection !== 'Dashboard' &&
            selectedSection !== 'Invoices' &&
            selectedSection !== 'Payments' &&
            selectedSection !== 'Credits' &&
            selectedSection !== 'Subscriptions' &&
            selectedSection !== 'Statements' &&
            selectedSection !== 'Payment Settings' &&
            selectedSection !== 'Expenses' && (
              <Typography variant="h5">{selectedSection} section is under development.</Typography>
            )}
        </Paper>
      </Grid>

      <Dialog open={!!selectedInvoice} onClose={handleClosePreview} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>{isCreditPreview ? 'Preview Credit' : 'Preview Invoice'}</DialogTitle>
        <DialogContent sx={{ minHeight: '70vh' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" align="center">
              {error}
            </Typography>
          ) : pdfData ? (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer
                fileUrl={pdfData}
                plugins={[defaultLayoutPluginInstance]}
              />
            </Worker>
          ) : (
            <Typography>Loading PDF...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
    </Box>
  );
};

export default Billing;